import {noop} from '@benchling/js-utils';
import {StyleSheet} from 'aphrodite';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface PurplePillProps {
  disabled?: boolean;
  hasError?: boolean;
  icon?: BenchlingIconName;
  onClick?: () => void;
  onDelete?: () => void;
  text: string;
  isRunning?: boolean;
}

export default function PurplePill({
  disabled = false,
  hasError = false,
  icon,
  onClick,
  onDelete,
  text,
  isRunning,
}: PurplePillProps): JSX.Element {
  return (
    <div
      className={css(
        styles.wrapper,
        !disabled && !isRunning && onClick != null ? styles.clickable : styles.unclickable
      )}
      onClick={disabled || isRunning ? noop : onClick}
      {...getTestProps({component: 'PurplePill', element: 'root', key: text})}
    >
      <div className={css(styles.pill, hasError && styles.errorPill, disabled && styles.disabledPill)}>
        {(icon != null || isRunning) && (
          <BenchlingIcon
            className={classNames(css(styles.columnTypeIcon), isRunning && 'fa-spin')}
            icon={isRunning ? 'spinner-third' : icon!}
          />
        )}
        <span className={css(styles.text)}>{text}</span>
        {!disabled && onDelete != null && (
          <BenchlingIcon
            className={css(styles.deleteIcon, hasError && styles.errorDeleteIcon)}
            icon="xmark"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  columnTypeIcon: {
    marginRight: '4px',
  },
  clickable: {
    cursor: 'pointer',
  },
  unclickable: {
    cursor: 'not-allowed',
  },
  wrapper: {
    display: 'inline-block',
    maxWidth: '100%',
  },
  pill: {
    color: Colors.PURPLE_DARK,
    backgroundColor: Colors.PURPLE_LIGHTEST,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
  },
  text: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  disabledPill: {
    backgroundColor: Colors.GRAY_LIGHTER,
    color: Colors.NEAR_BLACK,
  },
  errorPill: {
    color: Colors.RED_DARK,
    backgroundColor: Colors.RED_LIGHTEST,
  },
  deleteIcon: {
    marginLeft: 4,
    cursor: 'pointer',
    color: Colors.PURPLE_LIGHT,
    padding: 2,
    width: 14,
    height: 14,
    // Without this, the icon will shrink to fit the text and the hover background will be elliptical not circular
    flexShrink: 0,
    ':hover': {
      color: Colors.PURPLE,
      background: Colors.PURPLE_LIGHTER,
      borderRadius: '100%',
    },
  },
  errorDeleteIcon: {
    color: Colors.RED_DARK,
    ':hover': {
      color: Colors.RED_DARK,
      background: Colors.RED_LIGHTER,
      borderRadius: '100%',
    },
  },
});
